import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Eye, Users, History, Loader2, Mail, Monitor } from "lucide-react";
import api, { API } from "../lib/api";

export default function AdminCampaigns() {
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [bodyHtml, setBodyHtml] = useState(`<h2>New stock arrived at Peptigen Labs</h2>
<p>Fresh batches are in UK stock and ready for same-working-day dispatch before 14:00 GMT.</p>
<ul>
  <li><strong>GHK-Cu 50 mg</strong> — independently purity-tested, new lot</li>
  <li><strong>Retatrutide 10 mg</strong> — limited quantity available</li>
</ul>
<p><a style="color:#5EEAD4" href="https://peptigenlabs.co.uk/products">Browse current stock →</a></p>`);
  const [target, setTarget] = useState("newsletter");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [history, setHistory] = useState([]);
  const [recipientCount, setRecipientCount] = useState({ newsletter: 0, customers: 0 });
  const [previewHtml, setPreviewHtml] = useState("");
  const iframeRef = useRef(null);
  const previewDebounceRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/campaigns");
      setHistory(data || []);
    } catch (err) {
      console.error("[AdminCampaigns] failed to load history:", err?.message || err);
    }
  };

  const loadCounts = async () => {
    try {
      const [nlRes, ordRes] = await Promise.all([
        api.get("/admin/newsletter"),
        api.get("/admin/orders"),
      ]);
      const customerEmails = new Set(
        (ordRes.data || [])
          .filter((o) => o.payment_status === "paid")
          .map((o) => (o.shipping?.email || "").toLowerCase())
          .filter(Boolean)
      );
      setRecipientCount({
        newsletter: (nlRes.data || []).length,
        customers: customerEmails.size,
      });
    } catch (err) {
      console.error("[AdminCampaigns] failed to load recipient counts:", err?.message || err);
    }
  };

  useEffect(() => {
    loadHistory();
    loadCounts();
  }, []);

  // Debounced live render: re-renders the iframe preview 400ms after last change
  useEffect(() => {
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(
          "/admin/campaigns/render",
          { subject, preheader, body_html: bodyHtml },
          { responseType: "text", transformResponse: [(d) => d] }
        );
        setPreviewHtml(data);
      } catch (err) {
        // Preview render failed — keep the previous render visible rather
        // than wiping the iframe. Surface to console for debugging.
        console.debug("[AdminCampaigns] preview render failed:", err?.message || err);
      }
    }, 400);
    return () => previewDebounceRef.current && clearTimeout(previewDebounceRef.current);
  }, [subject, preheader, bodyHtml]);

  const sendPreview = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/campaigns/preview", {
        subject,
        preheader,
        body_html: bodyHtml,
        target,
      });
      toast.success(`Preview sent to ${data.sent_to}`);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const sendLive = async () => {
    const liveCount =
      target === "newsletter" ? recipientCount.newsletter :
      target === "customers" ? recipientCount.customers :
      recipientCount.newsletter + recipientCount.customers;
    const confirmed = window.confirm(
      `Send "${subject}" to approximately ${liveCount} recipient${liveCount === 1 ? "" : "s"}?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns/send", {
        subject,
        preheader,
        body_html: bodyHtml,
        target,
      });
      toast.success(`Sent to ${data.sent}/${data.recipient_count} recipients`);
      loadHistory();
    } catch (e) {
      toast.error("Campaign send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-campaigns-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="text-[#00C2D6]" size={28} />
          <h1 className="text-[#F7FAFC] pg-display text-3xl" style={{ fontFamily: "'Exo 2'" }}>
            Email campaigns
          </h1>
        </div>
        <p className="text-[#9CA8B8] text-sm max-w-2xl">
          Compose and broadcast to your newsletter subscribers or past customers. Preview hits{" "}
          <span className="text-[#5EEAD4]">info@peptigenlabs.co.uk</span> only; "Send Live" blasts the full segment.
          Unsubscribes are auto-suppressed.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Composer (LEFT) */}
        <div className="pg-card p-6 space-y-5">
          <div>
            <label className="pg-micro mb-2 block">// Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Fresh UK stock — GHK-Cu back with new batch"
              className="pg-input w-full"
              data-testid="campaign-subject"
            />
          </div>

          <div>
            <label className="pg-micro mb-2 block">// Preheader (inbox preview)</label>
            <input
              type="text"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="Short preview line shown next to the subject in Gmail / Apple Mail"
              className="pg-input w-full"
              data-testid="campaign-preheader"
            />
          </div>

          <div>
            <label className="pg-micro mb-2 block">// Body (HTML)</label>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              rows={14}
              className="pg-input w-full font-mono text-xs leading-relaxed"
              data-testid="campaign-body"
              spellCheck={false}
            />
            <div className="text-xs text-[#9CA8B8] mt-2">
              Supports <code className="text-[#5EEAD4]">&lt;h2&gt;</code>,{" "}
              <code className="text-[#5EEAD4]">&lt;p&gt;</code>,{" "}
              <code className="text-[#5EEAD4]">&lt;ul&gt;</code>,{" "}
              <code className="text-[#5EEAD4]">&lt;a&gt;</code>. Branded dark shell, ROU footer and one-click unsubscribe
              are added automatically. Keep it under ~300 words.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#1E2E44]">
            <button
              onClick={sendPreview}
              disabled={previewing}
              className="pg-btn-ghost"
              data-testid="campaign-preview-btn"
            >
              {previewing ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
              Send preview to info@
            </button>
            <button
              onClick={sendLive}
              disabled={sending}
              className="pg-btn-primary"
              data-testid="campaign-send-btn"
              style={{ background: "#A6FF00", color: "#0B1220" }}
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send live blast
            </button>
          </div>
        </div>

        {/* Live preview (RIGHT) */}
        <div className="pg-card p-4 flex flex-col" data-testid="campaign-live-preview">
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="flex items-center gap-2">
              <Monitor size={16} className="text-[#00C2D6]" />
              <h3 className="pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
                Live preview
              </h3>
            </div>
            <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4]">
              exactly what recipients see
            </span>
          </div>
          <div className="flex-1 rounded-sm overflow-hidden border border-[#1E2E44] bg-[#0B1220]" style={{ minHeight: "620px" }}>
            <iframe
              ref={iframeRef}
              title="email-preview"
              srcDoc={previewHtml || "<html><body style='background:#0B1220;color:#9CA8B8;font-family:Arial;padding:40px;text-align:center;'>Loading preview…</body></html>"}
              sandbox=""
              style={{ width: "100%", height: "100%", minHeight: "620px", border: 0, background: "#0B1220" }}
              data-testid="campaign-preview-iframe"
            />
          </div>
        </div>
      </div>

      {/* Segment + Auto-campaigns ROW below */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="space-y-5">
          <div className="pg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} className="text-[#00C2D6]" />
              <h3 className="pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
                Segment
              </h3>
            </div>
            <div className="space-y-2">
              {[
                { k: "newsletter", label: "Newsletter subscribers", count: recipientCount.newsletter },
                { k: "customers", label: "Past paid customers", count: recipientCount.customers },
                { k: "both", label: "Both (de-duplicated)", count: recipientCount.newsletter + recipientCount.customers },
              ].map((s) => (
                <label
                  key={s.k}
                  className={`flex items-center justify-between p-3 rounded-sm border cursor-pointer transition-colors ${
                    target === s.k
                      ? "border-[#A6FF00] bg-[rgba(166,255,0,0.05)]"
                      : "border-[#1E2E44] hover:border-[#00C2D6]/50"
                  }`}
                  data-testid={`campaign-target-${s.k}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={target === s.k}
                      onChange={() => setTarget(s.k)}
                      className="accent-[#A6FF00]"
                    />
                    <span className="text-sm text-[#F7FAFC]">{s.label}</span>
                  </div>
                  <span className="pg-mono text-xs text-[#A6FF00]">~{s.count}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={16} className="text-[#00C2D6]" />
              <h3 className="pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
                Auto campaigns
              </h3>
            </div>
            <div className="text-xs text-[#9CA8B8] space-y-2 leading-relaxed">
              <div>
                <strong className="text-[#F7FAFC]">Cart abandonment</strong>
                <br />
                <span className="text-[#5EEAD4]">● Active</span> — first nudge at 1 h, discount at 24 h.
              </div>
              <div>
                <strong className="text-[#F7FAFC]">Win-back</strong>
                <br />
                <span className="text-[#5EEAD4]">● Active</span> — fires after 60 days of inactivity with 10% code.
              </div>
              <div className="pt-2 border-t border-[#1E2E44] text-[11px]">
                Both sequences use Claude Haiku 4.5 to personalise copy per recipient.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="pg-card p-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <History size={16} className="text-[#00C2D6]" />
          <h3 className="pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
            Campaign history
          </h3>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-[#9CA8B8]">No campaigns sent yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="campaign-history-table">
            <thead>
              <tr className="text-left text-[#9CA8B8] border-b border-[#1E2E44]">
                <th className="pb-2 pg-mono text-[10px] uppercase tracking-[0.18em]">Sent</th>
                <th className="pb-2 pg-mono text-[10px] uppercase tracking-[0.18em]">Subject</th>
                <th className="pb-2 pg-mono text-[10px] uppercase tracking-[0.18em]">Target</th>
                <th className="pb-2 pg-mono text-[10px] uppercase tracking-[0.18em] text-right">Recipients</th>
                <th className="pb-2 pg-mono text-[10px] uppercase tracking-[0.18em] text-right">Delivered</th>
              </tr>
            </thead>
            <tbody>
              {history.map((c) => (
                <tr key={c.id} className="border-b border-[#1E2E44]/50">
                  <td className="py-2 text-[#9CA8B8] text-xs pg-mono">
                    {c.created_at?.slice(0, 10)}
                  </td>
                  <td className="py-2 text-[#F7FAFC]">{c.subject}</td>
                  <td className="py-2 text-[#5EEAD4] text-xs pg-mono uppercase">{c.target}</td>
                  <td className="py-2 text-[#F7FAFC] text-right pg-mono">{c.recipient_count}</td>
                  <td className="py-2 text-[#A6FF00] text-right pg-mono">{c.sent ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
